/** Figures we did not calculate — **ADR-0032 option 2, requirement CTX-04**.
 *
 * Its own module rather than four more hooks in `queries.ts`, for the same reason the figures live
 * in their own table: an imported figure is a **different kind of thing** from a calculated one, and
 * the one failure the whole decision exists to prevent is a screen rendering one as the other.
 * Keeping the types apart here means a component that wanted to pass an imported receipt to the
 * calculated receipt drawer would not compile.
 *
 * **The refusal's code travels with its sentence** (DOC-01). `unwrap` in `queries.ts` stringifies a
 * structured detail into `[object Object]`, which is how a perfectly good server message becomes
 * nothing a person can read. So the mutation here reads `detail.message` for the reader and keeps
 * `detail.error` for the screen to branch on, because a remedy derived from prose breaks the day
 * somebody improves the prose.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, keys } from "./client";
import { Refused, refusalFrom } from "./refusal";
import type { components } from "./schema";
import type { Scope2Method } from "./queries";

/** One disclosed figure, with whether this filing is reconciled against it. */
export type ImportedFigure = components["schemas"]["ImportedFigureOut"];

/** **Rule 3's second species.** No factor, no engine version, no activity record — and
 * `calculated` is the literal `false`, so a template cannot be handed one claiming otherwise. */
export type ImportedReceipt = components["schemas"]["ImportedReceiptChainOut"];

/** One disclosure where what was disclosed and what we compute disagree. */
export type Difference = components["schemas"]["DifferenceOut"];

/** Somebody looked at a difference and wrote down why it is there. */
export type DifferenceResolution = components["schemas"]["ResolutionOut"];

export interface RecordImported {
  disclosure_key: string;
  period_start: string;
  period_end: string;
  value?: string | null;
  unit?: string | null;
  narrative?: string | null;
  source_description: string;
}

/** A refusal with both halves: the code a screen branches on and the sentence a person reads.
 *
 * `Refused` is now the base and carries both, plus the status — which this class had no way to
 * know, so a 403 and a 422 and a proxy's 504 were one thing here. It keeps its own name because
 * `Imported.tsx` branches on `code` by name (`period_closed`, `already_recorded`).
 */
export class ImportRefused extends Refused {
  constructor(status: number, code: string, message: string) {
    super(status, code, message);
    this.name = "ImportRefused";
  }
}

function refused(response: Response, error: unknown, fallback: string): ImportRefused {
  const base = refusalFrom(response, error, fallback);
  return new ImportRefused(base.status, base.code || "import_refused", base.message);
}

/** What this entity has disclosed under this regime, in force — **across periods**.
 *
 * Not restricted to the filing's own window, because SECR's 20H asks for the *preceding* year: a
 * list clamped to the filing could never show a comparative at all. Each row says whether it is
 * the one this filing is reconciled against, which the screen has to be able to state — a
 * comparative that looked reconciled when nothing compared it would be the worst sentence on it.
 *
 * Refetched on mount rather than cached long, like the report beside it. A stale comparative next
 * to a fresh figure is the class of error this product is built to avoid.
 */
export function useImportedFigures(obligationId: string | undefined) {
  return useQuery({
    queryKey: ["imported", obligationId ?? "none"] as const,
    enabled: Boolean(obligationId),
    queryFn: async (): Promise<ImportedFigure[]> => {
      const { data, error, response } = await api.GET(
        "/api/v1/reports/obligations/{obligation_id}/imported",
        { params: { path: { obligation_id: obligationId as string } } },
      );
      if (error) throw refused(response, error, "What was previously disclosed could not be read.");
      return data ?? [];
    },
  });
}

/** Where what was disclosed and what we compute disagree, and which of those is explained.
 *
 * **Keyed on the Scope 2 method**, because our figure is different under each: a difference against
 * a location-based total is not a difference against a market-based one, and a cache that ignored
 * the basis would show one as the other when the top bar's toggle moved.
 */
export function useImportedDifferences(
  obligationId: string | undefined,
  method: Scope2Method,
) {
  return useQuery({
    queryKey: ["imported", "differences", obligationId ?? "none", method] as const,
    enabled: Boolean(obligationId),
    queryFn: async (): Promise<Difference[]> => {
      const { data, error, response } = await api.GET(
        "/api/v1/reports/obligations/{obligation_id}/imported/differences",
        {
          params: {
            path: { obligation_id: obligationId as string },
            query: { scope2_method: method },
          },
        },
      );
      if (error) throw refused(response, error, "The reconciliation could not be read.");
      return data ?? [];
    },
  });
}

/** One imported figure's receipt.
 *
 * **Cached indefinitely**, like a calculated receipt and for the same reason: an imported figure is
 * never edited — a correction is a new row that supersedes it (ADR-0030) — so what this returns
 * cannot change. The `superseded` flag on it *can*, which is why the figure list is refetched on
 * mount and this is not: the list is where a reader learns a figure was restated.
 */
export function useImportedReceipt(figureId: string | undefined) {
  return useQuery({
    queryKey: ["imported", "receipt", figureId ?? "none"] as const,
    enabled: Boolean(figureId),
    staleTime: Infinity,
    queryFn: async (): Promise<ImportedReceipt> => {
      const { data, error, response } = await api.GET("/api/v1/reports/imported/{figure_id}/receipt", {
        params: { path: { figure_id: figureId as string } },
      });
      if (error) throw refused(response, error, "That receipt could not be read.");
      if (!data) throw refused(response, error, "That receipt could not be read.");
      return data;
    },
  });
}

/** Hold one figure this entity previously disclosed.
 *
 * The entity and the regime are **not** parameters: the server derives both from the filing
 * obligation (ADR-0029), and the request body has no field for either. A client that could name an
 * entity could hold a comparative against a company this filing is not about, and the result would
 * look entirely plausible.
 *
 * Invalidates the figures, the differences **and the report**, because recording one can make a
 * filing not ready: a screen saying every check has passed beside a difference somebody just
 * created is the stale-figure failure ADR-0027 names, in its most embarrassing form.
 */
export function useRecordImported(obligationId: string | undefined, method: Scope2Method) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordImported): Promise<ImportedFigure> => {
      // A client-side precondition, not a response: status 422 says "this request was never
      // sendable" rather than implying a server answered it.
      if (!obligationId) throw new ImportRefused(422, "no_filing", "No filing is selected.");
      const { data, error, response } = await api.POST(
        "/api/v1/reports/obligations/{obligation_id}/imported",
        { params: { path: { obligation_id: obligationId } }, body: input },
      );
      if (error) throw refused(response, error, "That figure could not be recorded.");
      if (!data) throw refused(response, error, "That figure could not be recorded.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["imported"] });
      await queryClient.invalidateQueries({
        queryKey: keys.report(obligationId ?? "", method),
      });
    },
  });
}

/** Account for one difference. **Explains, never corrects.**
 *
 * The three values identify *which* difference and are sent as the screen read them, not as
 * whatever they happen to be when the request arrives: a resolution written against numbers the
 * resolver never saw would account for a gap nobody looked at. The server stores both values on the
 * row for the same reason, so a correction that moves either one makes this unresolved again.
 *
 * Invalidates the report as well as the differences, because this is what **releases** the filing —
 * and a verdict still reading "a figure disagrees with what was previously disclosed" after
 * somebody has just explained it is the product disagreeing with the person about what they did.
 */
export function useResolveDifference(obligationId: string | undefined, method: Scope2Method) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { figureId: string; difference: Difference; reason: string }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/reports/imported/{figure_id}/differences",
        {
          params: { path: { figure_id: input.figureId } },
          body: {
            disclosure_key: input.difference.disclosure_key,
            imported_value: input.difference.imported,
            our_value: input.difference.ours,
            unit: input.difference.unit,
            reason: input.reason,
          },
        },
      );
      if (error) throw refused(response, error, "That difference could not be explained.");
      return data ?? {};
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["imported"] });
      await queryClient.invalidateQueries({
        queryKey: keys.report(obligationId ?? "", method),
      });
    },
  });
}
